import { makeAutoObservable, runInAction } from 'mobx';
import IframeService from '../../../services/iframe-service/iframe-service';
import { rootContainer, TYPE } from '../../../services/container/root';
import {
    RUN_VARIANT,
    type ICreateRmaOutcome,
    type RunVariant,
} from './create-rma-submission-types';

export default class CreateRmaResponseStore {
    private readonly _outcomes: ICreateRmaOutcome[];
    private _timer: number = 6;
    private _abortTimer: boolean = false;

    constructor(outcomes: ICreateRmaOutcome[]) {
        makeAutoObservable(this, {}, { autoBind: true });
        this._outcomes = outcomes;
        if (this.hasCountdown) {
            this._startTimer();
        }
    }

    private get _iframeService(): IframeService {
        return rootContainer.get<IframeService>(TYPE.IframService);
    }

    public get variant(): RunVariant {
        const submitted = this.submittedCount;
        const created = this.createdCount;

        if (created === submitted) {
            return RUN_VARIANT.ALL_CREATED;
        }

        if (created === 0) {
            return RUN_VARIANT.NONE_CREATED;
        }

        return RUN_VARIANT.PARTIAL;
    }

    public get rows(): ICreateRmaOutcome[] {
        return this._outcomes;
    }

    public get createdCount(): number {
        return this._outcomes.filter((outcome) => outcome.isCreated).length;
    }

    public get submittedCount(): number {
        return this._outcomes.length;
    }

    /** ActionResultModal success indicator: failure only when nothing was created. */
    public get success(): boolean {
        return this.variant !== RUN_VARIANT.NONE_CREATED;
    }

    public get hasCountdown(): boolean {
        return this.variant === RUN_VARIANT.ALL_CREATED && this.createdCount === 1;
    }

    /** Single-outcome server title (AC1); multi-outcome modals use translation keys in the UI. */
    public get messageTitle(): string {
        if (this._outcomes.length === 1) {
            return this._outcomes[0].messageTitle ?? '';
        }

        return '';
    }

    public get message(): string {
        if (this._outcomes.length === 1) {
            return this._outcomes[0].message ?? '';
        }

        return '';
    }

    public get warningMessage(): string {
        if (this._outcomes.length === 1) {
            return this._outcomes[0].warningMessage ?? '';
        }

        return '';
    }

    public get rmaId(): string {
        return this.firstCreatedRmaId ?? '';
    }

    public get firstCreatedRmaId(): string | undefined {
        const firstCreated = this._outcomes.find((outcome) => outcome.isCreated);
        return firstCreated?.rmaId;
    }

    public openCreatedRma(): void {
        this.abortTimer();
        const rmaId = this.rmaId;

        if (rmaId) {
            this._iframeService.redirectParentFrame('/dashboard/returns?rmaid=' + rmaId);
            return;
        }

        this._iframeService.redirectParentFrame('/dashboard/returns');
    }

    private _startTimer(): void {
        const interval = setInterval(() => {
            runInAction(() => {
                this._timer--;
            });

            if (this._abortTimer) {
                clearInterval(interval);
            }

            if (this._timer === 0 && !this._abortTimer) {
                this.openCreatedRma();
                clearInterval(interval);
            }
        }, 1000);
    }

    public getTimer(): string {
        if (!this.hasCountdown) {
            return '';
        }

        return this._timer > 0 ? `(${this._timer})` : '';
    }

    public abortTimer(): void {
        this._abortTimer = true;
    }
}
